import { Link, NavLink } from "react-router-dom";
import { useEffect, useState } from "react";
import { useI18n } from "@/lib/i18n";
import { useCart } from "@/lib/cart";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import {
    ShoppingCart,
    Menu,
    X,
    Package,
    User,
    LogOut,
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import CartDrawer from "./CartDrawer";
import { FaWhatsapp } from "react-icons/fa";

const navItems = [
    { to: "/", key: "nav_home" },
    { to: "/books/written", key: "nav_written" },
    { to: "/books/mcq", key: "nav_mcq" },
    { to: "/books/ebook", key: "nav_ebook" },
    { to: "/blog", key: "nav_blog" },
    { to: "/winners", key: "nav_winner" },
    { to: "/offline-store", key: "nav_offline" },
];

export default function Navbar() {
    const { t } = useI18n();
    const { count } = useCart();
    const { user, logout } = useAuth();

    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const [loggingOut, setLoggingOut] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 80);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        if (!userMenuOpen) return;
        const handler = (e) => {
            setUserMenuOpen(false);
        };
        const timer = setTimeout(() => {
            document.addEventListener("click", handler);
        }, 0);
        return () => {
            clearTimeout(timer);
            document.removeEventListener("click", handler);
        };
    }, [userMenuOpen]);

    const handleLogout = async () => {
        if (loggingOut) return;
        setLoggingOut(true);
        setUserMenuOpen(false);
        try {
            await logout();
        } finally {
            setLoggingOut(false);
        }
    };

    return (
        <>
            {/* ===== TOP STRIP (NOT STICKY) ===== */}
            <div className="bg-[#08145A] text-white text-xs">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between">

                    {/* Whatsup */}
                    <div className="flex items-center gap-2 text-white/80">
                        <Link
                            to="https://wa.me/8801558997668"
                            className="flex items-center gap-2 hover:text-white transition-colors"
                            target="_blank"
                        >
                            <FaWhatsapp className="text-lg" />
                            <span>+8801558997668</span>
                        </Link>
                    </div>

                    {/* Right menu */}
                    <div className="flex items-center gap-3">
                        <Link
                            to="/track"
                            className="hover:text-[#AFC4D6] inline-flex items-center gap-1"
                        >
                            <Package className="w-3.5 h-3.5" />
                            {t("track_order")}
                        </Link>


                    </div>
                </div>
            </div>

            {/* ===== BRAND ROW (STICKY) ===== */}
            <div
                className={`sticky top-0 z-50 transition-all duration-300 ${
                    scrolled ? "shadow-md backdrop-blur bg-white/95" : "bg-white"
                }`}
            >
                <div className="border-b border-brand-light">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-center lg:justify-between w-full gap-4">

                        {/* Logo */}
                        <Link to="/" className="flex items-center gap-2 group">
                            <img src="/logo.png" alt="logo" />
                        </Link>

                        {/* Desktop Nav */}
                        <ul className="hidden lg:flex items-center gap-1 overflow-x-auto">
                            {navItems.map((it) => (
                                <li key={it.to}>
                                    <NavLink
                                        to={it.to}
                                        end={it.to === "/"}
                                        className={({ isActive }) =>
                                            `inline-block px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                                                isActive
                                                    ? "text-brand-royal border-[#D61F1F]"
                                                    : "text-brand-navy/80 border-transparent hover:text-brand-royal hover:border-brand-royal/40"
                                            }`
                                        }
                                    >
                                        {t(it.key)}
                                    </NavLink>
                                </li>
                            ))}
                        </ul>

                        {/* Right Actions */}
                        <div className="hidden lg:flex items-center gap-2">

                            {/* User icon — auth aware */}
                            {user ? (
                                <div
                                    className="relative"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <button
                                        onClick={() => setUserMenuOpen((v) => !v)}
                                        className="flex items-center gap-2 p-1.5 pr-3 rounded-xl border border-brand-light hover:bg-brand-light/40 text-brand-navy transition-colors"
                                    >
                                        <div className="w-7 h-7 rounded-lg bg-[#0B1E8A] text-white flex items-center justify-center text-xs font-bold uppercase">
                                            {user.name?.charAt(0) || "U"}
                                        </div>
                                        <span className="text-sm font-semibold text-brand-navy max-w-[90px] truncate">
                                            {user.name?.split(" ")[0]}
                                        </span>
                                    </button>

                                    {userMenuOpen && (
                                        <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-2xl shadow-lg border border-brand-light py-1.5 z-50">
                                            <div className="px-4 py-2 border-b border-brand-light mb-1">
                                                <p className="text-xs font-bold text-brand-navy truncate">{user.name}</p>
                                                <p className="text-xs text-slate-400 truncate">{user.email}</p>
                                            </div>
                                            <Link
                                                to="/profile"
                                                onClick={() => setUserMenuOpen(false)}
                                                className="flex items-center gap-2 px-4 py-2 text-sm text-brand-navy hover:bg-brand-light/50 transition-colors"
                                            >
                                                <User className="w-4 h-4" />
                                                My Profile
                                            </Link>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleLogout();
                                                }}
                                                disabled={loggingOut}
                                                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-[#D61F1F] hover:bg-red-50 transition-colors disabled:opacity-50"
                                            >
                                                <LogOut className="w-4 h-4" />
                                                {loggingOut ? "Logging out…" : "Logout"}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <Link
                                    to="/login"
                                    className="p-2 rounded-lg border border-brand-light hover:bg-brand-light/40 text-brand-navy"
                                >
                                    <User className="w-5 h-5" />
                                </Link>
                            )}

                            {/* Cart */}
                            <CartDrawer>
                                <button className="relative p-2 rounded-lg border border-brand-light hover:bg-brand-light/40 text-brand-navy">
                                    <ShoppingCart className="w-5 h-5" />
                                    {count > 0 && (
                                        <span className="absolute -top-1.5 -right-1.5 bg-[#D61F1F] text-white text-[10px] font-bold rounded-full min-w-[20px] h-5 px-1 flex items-center justify-center border-2 border-white shadow-pop">
                                            {count}
                                        </span>
                                    )}
                                </button>
                            </CartDrawer>

                            {/* Mobile Menu */}
                            <Sheet open={open} onOpenChange={setOpen}>
                                <SheetTrigger asChild>
                                    <Button variant="ghost" className="lg:hidden" size="icon">
                                        {open ? <X /> : <Menu />}
                                    </Button>
                                </SheetTrigger>

                                <SheetContent side="right" className="w-72 font-bn">
                                    <div className="flex flex-col gap-1 mt-8">
                                        {navItems.map((it) => (
                                            <NavLink
                                                key={it.to}
                                                to={it.to}
                                                end={it.to === "/"}
                                                onClick={() => setOpen(false)}
                                                className={({ isActive }) =>
                                                    `px-3 py-2 rounded-lg ${
                                                        isActive
                                                            ? "bg-brand-light text-brand-royal font-semibold"
                                                            : "text-brand-navy hover:bg-brand-light/40"
                                                    }`
                                                }
                                            >
                                                {t(it.key)}
                                            </NavLink>
                                        ))}

                                        {/* Mobile এও logout বোতাম */}
                                        {user && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setOpen(false);
                                                    handleLogout();
                                                }}
                                                disabled={loggingOut}
                                                className="mt-2 flex items-center gap-2 px-3 py-2 rounded-lg text-[#D61F1F] hover:bg-red-50 text-sm font-medium disabled:opacity-50"
                                            >
                                                <LogOut className="w-4 h-4" />
                                                {loggingOut ? "Logging out…" : "Logout"}
                                            </button>
                                        )}
                                    </div>
                                </SheetContent>
                            </Sheet>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}